/**
 * Template: Login
 * ------------------------------------------------------------------------------
 * A file that contains scripts highly couple code to the Login template.
 *
 * @namespace login
 */
import {focusable} from '~/helpers/a11y';
import cssClasses from '~/helpers/cssClasses';
import {on} from '~/helpers/utils';

import '../../../styles/templates/customers/account.scss';
import '../../../styles/templates/customers/login.scss';
import '../../../styles/sections/login-block.scss';
import '../../../styles/sections/login-form.scss';

/**
 * DOM selectors.
 */
const selectors = {
  customerLoginForm: '[js-password-form="customerLoginForm"]',
  cancelPasswordLink: '[js-password-form="cancelPasswordLink"]',
  recoverPasswordForm: '[js-password-form="recoverPasswordForm"]',
  resetPasswordSuccess: '[js-password-form="resetPasswordSuccess"]',
  forgotPasswordLink: '[js-password-form="forgotPasswordLink"]',
  registerLink: '[js-register="register"]',
  loginForm: '#customer_login',
  passwordForm: '[js-password-form="recoverPasswordForm"] form',
};

/**
 * Create a new login password form instance.
 */
export function PasswordForm() {

  /**
   * DOM Node selectors.
   */
  const nodeSelectors = {
    recoverPasswordForm: document.querySelector(selectors.recoverPasswordForm),
    customerLoginForm: document.querySelector(selectors.customerLoginForm),
    resetPasswordSuccess: document.querySelector(selectors.resetPasswordSuccess),
    cancelPasswordLink: document.querySelector(selectors.cancelPasswordLink),
    forgotPasswordLink: document.querySelector(selectors.forgotPasswordLink),
    registerLink: document.querySelector(selectors.registerLink),
    loginForm: document.querySelector(selectors.loginForm),
    passwordForm: document.querySelector(selectors.passwordForm),
  };

  const customerLoginFocusables = focusable(nodeSelectors.customerLoginForm);
  const recoverPasswordFocusables = focusable(nodeSelectors.recoverPasswordForm);

  /**
   * Initialise login form.
   */
  function init() {
    setEventListeners();

    /** Remove the localstorage if wrong password and for password reset */
    localStorage.removeItem('loginForm');

    if (localStorage.getItem('passwordForm')) {
      localStorage.removeItem('passwordForm');

      if (location.hash.substr(1) !== 'recover') {
        window.dataLayer.push({
          event: 'GAevent',
          eventID: '10',
          eventCategory: 'account',
          eventAction: 'reset_pwd',
          eventLabel: 'step2',
        });
      }
    }

    if (nodeSelectors.forgotPasswordLink) {
      checkUrlHash();
      resetPasswordSuccess();
    }
  }

  /**
   * Set toggle events on forgot password and cancel links.
   */
  function setEventListeners() {
    on('click', nodeSelectors.forgotPasswordLink, (event) => {
      handlePasswordToggle(event);

      window.dataLayer.push({
        event: 'GAevent',
        eventID: '10',
        eventCategory: 'account',
        eventAction: 'reset_pwd',
        eventLabel: 'step1',
      });
    });

    on('click', nodeSelectors.cancelPasswordLink, (event) => handlePasswordToggle(event));

    on('submit', nodeSelectors.loginForm, () => handleLoginDatalayer());

    on('submit', nodeSelectors.passwordForm, () => handlePasswordDatalayer());

    on('click', nodeSelectors.registerLink, () => {
      window.dataLayer.push({
        event: 'GAevent',
        eventID: '09',
        eventCategory: 'account',
        eventAction: 'registration',
        eventLabel: 'step1',
      });
    });
  }

  /**
   * Handle new localStorage element to check login.
   */
  function handleLoginDatalayer() {
    localStorage.setItem('loginForm', 'step2');
  }

  /**
   * Handle new localStorage element to check password reset.
   */
  function handlePasswordDatalayer() {
    localStorage.setItem('passwordForm', 'step2');
  }

  /**
   * Handle login and forgot password form toggle.
   */
  function handlePasswordToggle(event) {
    event.preventDefault();
    toggleRecoverPasswordForm();
  }

  /**
   * Check if #recover is in url hash.
   * - This can occur when the user is coming from an external source.
   */
  function checkUrlHash() {
    if (window.location.hash === '#recover') {
      toggleRecoverPasswordForm();
    }
  }

  /**
   *  Show/Hide recover password form.
   */
  function toggleRecoverPasswordForm() {
    nodeSelectors.recoverPasswordForm.classList.toggle(cssClasses.hidden);
    nodeSelectors.customerLoginForm.classList.toggle(cssClasses.hidden);

    if (!nodeSelectors.recoverPasswordForm.classList.contains(cssClasses.hidden)) {
      customerLoginFocusables.forEach((element) => element.setAttribute('tabindex', -1));
      recoverPasswordFocusables.forEach((element) => element.setAttribute('tabindex', 0));
      recoverPasswordFocusables[0].focus();
      return;
    }

    customerLoginFocusables.forEach((element) => element.setAttribute('tabindex', 0));
    recoverPasswordFocusables.forEach((element) => element.setAttribute('tabindex', -1));
    customerLoginFocusables[0].focus();
  }

  /**
   *  Show reset password success message if posted successfully.
   */
  function resetPasswordSuccess() {
    if (!nodeSelectors.resetPasswordSuccess) {
      return;
    }

    nodeSelectors.resetPasswordSuccess.classList.remove(cssClasses.hidden);
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  });
}

document.addEventListener('DOMContentLoaded', () => {
  PasswordForm().init();
});
